import { ampli } from '../../../ampli';
import { useState, useCallback, useEffect } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Modal';
import { API, ShareAPI } from '../../../API';
import { Paginator } from '../../../components/Paginator/Paginator';
import {
  Feedback,
  feedbackContexts,
} from '../../../components/Feedback/Feedback';
import { ReactComponent as AddToIcon } from '../../../assets/addTo.svg';
import { ReactComponent as ShareThreatIcon } from '../../../assets/shareThreat.svg';
import {
  platformNamesMapping,
} from '../../../utils/platforms';
import Tooltip from '../../../components/Tooltip/Tooltip';
import { MessageTooltips } from '../../../components/MessagesTable/MessageTooltips';
import { useCurrentUser } from '../../../contexts/CurrentUser';
import { ExpandableText } from '../../../components/CollapsibleText/ExpandableText';
import { Loader } from '../../../components/Loader/Loader';
import { LoaderSmall } from '../../../components/LoaderSmall/LoaderSmall';
import { replaceNewlinesWithBreaks } from '../../../utils/replaceNewlinesWithBreaks';
import { ImagesContainer } from '../../../components/Image/ImagesContainer';
import { TranslationSwitch } from '../../../components/TranslationSwitch/TranslationSwitch';
import { formatNumber } from '../../../utils/fomatNumbers';
import { showRequestError } from '../../../utils/requestError';
import { MessageInfoWrapper } from '../../../components/MessagesTable/MessageInfoWrapper';
import { MessageMetricsWrapper } from '../../../components/MessagesTable/MessageMetricsWrapper';
import { OriginalTextBlock } from '../../../components/MessagesTable/OriginalTextBlock';

import messageStyles from '../../../components/MessagesTable/ExtendedMessageView.module.scss';

import styles from '../NarrativePage.module.scss';

const sortingFieldNames = {
  DATE: 'date_publicated',
  SOURCE: 'source',
  MESSAGE: 'text',
  VIEWS: 'impressions',
  REACTIONS: 'engagement',

  AGE: 'user_age',
  LOCATION: 'user_location',
  GROUPS: 'user_groups',
};

export const NarrativeStoriesModal = ({
  infoModal,
  type = null,
  subType = null,
  countryId = null,
  narrative,
  onChange,
  isShare = false,
  onAddToCase,
  onAddToThreat,
}) => {
  const { t } = useTranslation();

  const sourcesTypes = [
    { value: null, label: t('All') },
    { value: 'is_verified', label: t('Verified') },
    { value: 'influential', label: t('Influential') },
    { value: 'state_affiliated', label: t('State-affiliated') },
    { value: 'discreditated', label: t('Compromised accounts') },
    { value: 'is_bot', label: t('Bots') },
  ];

  const platformOptions = [
    { value: null, label: t('All') },
    { value: 'TELEGRAM', label: platformNamesMapping['TELEGRAM'] },
    { value: 'TIKTOK', label: platformNamesMapping['TIKTOK'] },
    { value: 'VK', label: platformNamesMapping['VK'] },
    { value: 'TWITTER', label: platformNamesMapping['TWITTER'] },
    { value: 'WEB', label: platformNamesMapping['WEB'] },
    
  ];

  const [currentUser] = useCurrentUser();
  const nativeLang = currentUser?.translation_language ? currentUser?.translation_language : window.clientInformation.language.split('-', 1)[0].toUpperCase();

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const showSiilaritySearch = featureFlags?.includes('show_similarity_search');

  const [platform, setPlatform] = useState(() => platformOptions[0]);
  const [sourceType, setSourceType] = useState(type || sourcesTypes[0]);
  const [countryID, setCountryID] = useState(countryId || null);
  const [sourceSubType, setSourceSubType] = useState(subType || null);
  const [messages, setMessages] = useState(null);
  const [messagesSorting, setMessagesSorting] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isMessageDropdownOpen, setIsMessageDropdownOpen] = useState({});
  const [isBulkTranslation, setIsBulkTranslation] = useState(false);
  const [isBulkTranslationLoading, setIsBulkTranslationLoading] = useState(false);

  const statusMapping = {
    NEW: { class: messageStyles.new, name: 'New' },
    READ: { class: messageStyles.approved, name: 'Approved' },
    DELETED: { class: messageStyles.deleted, name: 'Deleted' },
  };

  const [highlights, setHighlights] = useState(['KEYWORDS']);

  const api = isShare ? ShareAPI : API;

  const fetchMessages = useCallback(
    (
      sourceType,
      sourceSubType,
      platform,
      countryID,
      messagesSorting,
      page = 1,
    ) => {
      if (!narrative) {
        return;
      }

      setIsLoading(true);

      const urlParams = new URLSearchParams();
      urlParams.set('narrative_id', narrative.id);
      urlParams.set('size', '10');
      urlParams.set('page', page);
      urlParams.set('narrative_story_id', infoModal.id);

      const messagesSortingQuery = messagesSorting
        ? `${messagesSorting.isAscending ? '' : '-'}${
            messagesSorting.fieldName
          }`
        : null;

      if (messagesSorting) {
        urlParams.set('sorting', messagesSortingQuery);
      }

      if (sourceType.value) {
        urlParams.set(sourceType.value, true);
      }

      if (sourceSubType) {
        if (sourceType.value === 'discreditated') {
          urlParams.set('entity_type', sourceSubType);
        } else {
          urlParams.set(sourceSubType, true);
        }
      }

      if (platform.value) {
        urlParams.set('source_types', platform.value);
      }

      if (countryID) {
        urlParams.set('country_id', countryID);
      }

      api.fetch('GET', `/API/v1/messages?${urlParams.toString()}`).then(
        (data) => {
          setMessages(data);
          setIsLoading(false);
        },
      ).catch((e) => {
        setIsLoading(false);
        showRequestError(e, t('Stories'));
      });
    },
    [],
  );

  useEffect(() => {
    fetchMessages(
      sourceType,
      sourceSubType,
      platform,
      countryID,
      messagesSorting,
      page,
    );
  }, [platform, sourceSubType, sourceType, countryID, messagesSorting, page]);

  const select = (
    <Select
      className="chart-select"
      onChange={(e) => {
        setPlatform(e);
        setCountryID(null);
        setSourceSubType(null);
      }}
      value={platform}
      options={platformOptions}
    />
  );

  const typesSelect = (
    <Select
      className={styles.chartSelect}
      classNamePrefix="chartSelect"
      onChange={(e) => {
        setSourceType(e);
        setCountryID(null);
        setSourceSubType(null);
      }}
      value={sourceType}
      options={sourcesTypes}
    />
  );

  const handleMessagesSortingClick = (fieldName) => {
    const isAscending =
      messagesSorting && messagesSorting.fieldName === fieldName
        ? !messagesSorting.isAscending
        : true;

    setMessagesSorting({
      isAscending,
      fieldName: fieldName,
    });
  };

  const deleteStoryMessage = (narrativeId, storyId, messageId) => {
    API.fetch(
      'DELETE',
      `/API/v1/narrative_stories/${narrativeId}/stories/${storyId}/messages/${messageId}`,
    )
      .then(() => {
        fetchMessages(
          sourceType,
          sourceSubType,
          platform,
          countryID,
          messagesSorting,
          page,
        );
      })
      .catch((e) => {
        showRequestError(e, t('Stories'));
      });
  }

  const handleBulkTranslation = async (messagesToTranslate) => {
    const BATCH_SIZE = 5;
    const nativeLanguage = currentUser?.translation_language
      ? currentUser?.translation_language
      : window.clientInformation.language.split('-', 1)[0];

    const batches = [];
    for (let i = 0; i < messagesToTranslate.length; i += BATCH_SIZE) {
      batches.push(messagesToTranslate.slice(i, i + BATCH_SIZE));
    }

    for (const batch of batches) {
      await processBatch(batch, nativeLanguage);
    }
  };

  const processBatch = async (batch, nativeLanguage) => {
    const translationPromises = batch.map(message => {
      const originalPostText = message.content_type === 'COMMENT' ? replaceNewlinesWithBreaks(message.parent_message?.highlighted_text || message.parent_message?.text) : '';
      const textToTranslate = message.highlighted_text ? replaceNewlinesWithBreaks(message.highlighted_text) : replaceNewlinesWithBreaks(message.text);
      
      if (originalPostText) {
        return Promise.allSettled([
          translateMessage(message.id, originalPostText, nativeLanguage),
          translateMessage(message.id, textToTranslate, nativeLanguage),
        ]).then(results => ({
          messageId: message.id,
          type: 'comment',
          parentResult: results[0],
          messageResult: results[1]
        })); 
      } else {
        return translateMessage(message.id, textToTranslate, nativeLanguage)
          .then(data => ({
            messageId: message.id,
            type: 'message',
            messageResult: { status: 'fulfilled', value: data }
          }))
          .catch(error => ({
            messageId: message.id,
            type: 'message',
            messageResult: { status: 'rejected', reason: error }
          }));
      }
    });

    const batchResults = await Promise.allSettled(translationPromises);

    setMessages(prevMessages => {
      let newMessagesList = [...prevMessages.objects];

      batchResults.forEach(result => {
        if (result.status === 'fulfilled') {
          const { messageId, type, parentResult, messageResult } = result.value;

          newMessagesList = newMessagesList.map(message => {
            if (message.id === messageId) {
              let updatedMessage = {
                ...message,
                isTranslation: true,
              };

              if (type === 'comment' && parentResult) {
                if (parentResult.status === 'fulfilled') {
                  updatedMessage.parent_message = {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: parentResult.value.destination_text,
                  };
                } else {
                  updatedMessage.parent_message = {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  };
                }
              }

              if (messageResult.status === 'fulfilled') {
                updatedMessage.translated = messageResult.value.destination_text;
              } else {
                updatedMessage.translated = t('We were not able to translate this text.');
                updatedMessage.error = true;
              }

              return updatedMessage;
            }
            return message;
          });
        }
      });

      return { ...prevMessages, objects: newMessagesList };
    });
  };

  const toggleAllToTranslation = (messageIds) => {
    setMessages(prevMessages => ({
      ...prevMessages,
      objects: prevMessages.objects.map(message => {
        if (messageIds.includes(message.id)) {
          return {
            ...message,
            isTranslation: true,
            ...(message.content_type === 'COMMENT' && message.parent_message && {
              parent_message: {
                ...message.parent_message,
                isTranslation: true,
              }
            })
          };
        }
        return message;
      })
    }));
  };

  const toggleAllToOriginal = (messageIds) => {
    setMessages(prevMessages => ({
      ...prevMessages,
      objects: prevMessages.objects.map(message => {
        if (messageIds.includes(message.id)) {
          return {
            ...message,
            isTranslation: false,
            ...(message.content_type === 'COMMENT' && message.parent_message && {
              parent_message: {
                ...message.parent_message,
                isTranslation: false,
              }
            })
          };
        }
        return message;
      })
    }));
  };

  const handleBulkTranslateClick = async () => {
    setIsBulkTranslationLoading(true);

    const messagesToTranslate = [];
    const alreadyTranslated = [];

    messages.objects.forEach(message => {
      if (message.translated) {
        alreadyTranslated.push(message);
      } else {
        messagesToTranslate.push(message);
      }
    });
    
    if (messagesToTranslate.length === 0) {
      console.log('No messages to translate');
      const messageIds = alreadyTranslated.map(msg => msg.id);
      toggleAllToTranslation(messageIds);
      setIsBulkTranslationLoading(false);
      return;
    }

    if ( alreadyTranslated.length > 0) {
      toggleAllToTranslation(alreadyTranslated.map(msg => msg.id));
    }

    const messageIds = messagesToTranslate.map(msg => msg.id);
    toggleAllToTranslation(messageIds);


    try {
      await handleBulkTranslation(messagesToTranslate);
      setIsBulkTranslationLoading(false);
    } catch (error) {
      setIsBulkTranslationLoading(false);
      console.error('Error during bulk translation:', error);
    }
  };

  const handleShowOriginalClick = () => {
    const translatedMessages = messages.objects.filter(message => message.translated);
    const messageIds = translatedMessages.map(msg => msg.id);
    
    toggleAllToOriginal(messageIds);
    setIsBulkTranslationLoading(false);
  };

  const translateMessage = (messageId, text, nativeLang, sourceLang = '') => {
    let body;
    if (sourceLang) {
      body = {
        text: text,
        destination_language: nativeLang,
        source_language: sourceLang,
      };
    } else {
      body = {
        text: text,
        destination_language: nativeLang,
      };
    }
    return API.fetch('POST', '/API/v1/translations/translate', null, body);
  };

  const toggleTranslate = (messageId, isTranslation) => {
    setMessages({
      ...messages,
      objects: messages.objects.map((message) => {
        if (message.id === messageId) {
          message.isTranslation = isTranslation;
          if (message.content_type === 'COMMENT' && message.parent_message) {
            message.parent_message.isTranslation = isTranslation;
          }
          return message;
        } else {
          return message;
        }
      }),
    });
  };

  const handleTranslation = (messageId, text, isTranslation) => {
    const target = messages.objects.find((message) => message.id === messageId);
    const originalPostText =
      target?.content_type === 'COMMENT' ? target?.parent_message?.highlighted_text || target?.parent_message?.text : '';
    if (target.translated) {
      toggleTranslate(messageId, isTranslation);
    } else {
      toggleTranslate(messageId, true);

      const nativeLanguage = currentUser?.translation_language
        ? currentUser?.translation_language
        : narrative.parameters?.translate_keywords_query
        ? narrative.parameters?.keywords_query_origin_language
        : window.clientInformation.language.split('-', 1)[0];

      let newMessagesList = [...messages.objects];

      if (originalPostText) {
        Promise.allSettled([
          translateMessage(messageId, originalPostText, nativeLanguage),
          translateMessage(messageId, text, nativeLanguage),
        ]).then((results) => {
          if (results[0].status === 'fulfilled') {
            const originalPostData = results[0].value;

            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                let m = {
                  ...message,
                  parent_message: {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: originalPostData.destination_text,
                  },
                };
                return m;
              } else {
                return message;
              }
            });
          } else {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  parent_message: {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  },
                };
              } else {
                return message;
              }
            });
          }

          if (results[1].status === 'fulfilled') {
            const messageData = results[1].value;
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: messageData.destination_text,
                };
              } else {
                return message;
              }
            });
          } else {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: t('We were not able to translate this text.'),
                  error: true,
                };
              } else {
                return message;
              }
            });
          }

          setMessages({ ...messages, objects: newMessagesList });
        });
      } else {
        translateMessage(messageId, text, nativeLanguage)
          .then((data) => {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: data.destination_text,
                };
              } else {
                return message;
              }
            });

            setMessages({ ...messages, objects: newMessagesList });
          })
          .catch((e) => {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: t('We were not able to translate this text.'),
                  error: true,
                };
              } else {
                return message;
              }
            });

            setMessages({ ...messages, objects: newMessagesList });
          });
      }
    }
  };


  return (
    <Modal
      isVisible={infoModal.isActive}
      title={`${infoModal.title}...`}
      content={
        <>
          {/* <div className={styles.modalSelects}>
            {select}
            {typesSelect}
          </div> */}

          {!messages || isLoading ? (
            <Loader />
          ) : messages.objects.length > 0 ? (
            <div>
              <table className={messageStyles.extendedMessages} id='storiesModal'>
                <thead>
                  <tr>
                    <td className={messageStyles.headerInfo}>
                      <span>
                        {t('Total publications')}: {formatNumber(messages.total)}
                      </span>
                    </td>
                    <td className="message-controlls">
                      <div className={messageStyles.translationSwitchWrapper}>
                        <TranslationSwitch
                          isBulkTranslation={true}
                          isTranslation={isBulkTranslation}
                          isLoading={isBulkTranslationLoading}
                          theme='dark'
                          size='medium'
                          handleTranslation={(bool) => {
                            if (bool) {
                              setIsBulkTranslation(bool);
                              handleBulkTranslateClick();
                              ampli.track({
                                event_type: 'Bulk Translation Click',
                                event_properties: {
                                  user_id: currentUser?.id,
                                  workspace_id: currentUser?.workspace_id,
                                  narrative_id: narrative.id,
                                  event_location: 'Case: stories tab modal',
                                },
                              });
                            } else {
                              setIsBulkTranslation(false);
                              handleShowOriginalClick();
                            }
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {messages.objects.map((message) => {
                    let followers_cnt = message?.source?.audience;
                    let textClass = styles.extendedMessage;
                    let textClassParent = messageStyles.extendedMessage;

                    if (message.parent_message?.error && message.parent_message?.isTranslation) {
                      textClassParent = `${messageStyles.extendedMessage} ${messageStyles.error}`
                    } else if (message.error && !message.isTranslation) {
                      textClassParent = messageStyles.extendedMessage;
                    }

                    if (message.error && message.isTranslation) {
                      textClass = `${styles.extendedMessage} ${styles.error}`;
                    } else if (message.error && !message.isTranslation) {
                      textClass = styles.extendedMessage;
                    }

                    let messageMenuOptions = []
                    if(showSiilaritySearch) {
                      messageMenuOptions.push({
                        name: t('Find similar publications'),
                        value: 'similar'
                      })
                    }

                    if((message?.content_type === 'COMMENT' && (message?.parent_message || message?.parent_id)) || message?.content_type === 'POST') {
                      messageMenuOptions.push({
                        name: t('Find comments to post'),
                        value: 'comments'
                      })
                    }

                    if (message?.content_type === 'VIDEO' && message?.source?.source_type === 'TIKTOK') {
                      messageMenuOptions.push({
                        name: t('Find comments to video'),
                        value: 'comments_to_video'
                      })
                    }

                    if (message?.content_type === 'POST' && ['TIKTOK', 'FACEBOOK', 'TELEGRAM'].includes(message?.source?.source_type)) {
                      messageMenuOptions.push({
                        name: t('Find reposts to post'),
                        value: 'reposts_to_post'
                      })
                    }

                    const textForTranslation = message.highlighted_text ? replaceNewlinesWithBreaks(message.highlighted_text) : replaceNewlinesWithBreaks(message.text);

                    return (
                      <tr key={message.id}>
                        <td>
                          <MessageInfoWrapper
                            message={message}
                            followers_cnt={followers_cnt}
                            isShare={isShare}
                            currentUser={currentUser}
                            narrative={narrative}
                          />

                          <div className={messageStyles.messageContainer}>
                            <MessageMetricsWrapper
                              message={message}
                              narrative={narrative}
                              isShare={isShare}
                              isMessageDropdownOpen={isMessageDropdownOpen}
                              setIsMessageDropdownOpen={setIsMessageDropdownOpen}
                              messageMenuOptions={messageMenuOptions}
                            />

                            {message.parent_message ? (
                                <OriginalTextBlock
                                  message={message}
                                  isShare={isShare}
                                  currentUser={currentUser}
                                  narrative={narrative}
                                  textClassParent={textClassParent}
                                />
                              ) : (
                                ''
                              )}

                            {message?.images && message?.images?.length > 0 ? (
                              <ImagesContainer
                                images={message?.images}
                                showCount={3}
                                className={styles.imagesContainer}
                              />
                            ) : (
                              ''
                            )}

                            <div className={messageStyles.textBlock}>
                              {message.isTranslation === true &&
                              !message.translated ? (
                                <div className={messageStyles.loaderContainer}>
                                  <LoaderSmall />
                                </div>
                              ) : (
                                <ExpandableText
                                  length={150}
                                  highlights={highlights}
                                  text={
                                    message.isTranslation
                                      ? message.translated
                                      : message.highlighted_text || message.text
                                  }
                                  textClassName={textClass}
                                  onExpand={(expanded) => {
                                    ampli.track({
                                      event_type: expanded
                                        ? 'Expand original post in-place'
                                        : 'Collapse original post in-place',
                                      event_properties: {
                                        user_id: currentUser?.id,
                                        workspace_id: currentUser?.workspace_id,
                                        narrative_id: narrative?.id,
                                      },
                                    });
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="message-controlls">
                          <div className={messageStyles.controls}>
                            <Feedback
                              context={feedbackContexts.STORIES_MESSAGE}
                              feedbackParams={{
                                narrative_id: narrative.id,
                                story_id: infoModal.id,
                                selected_message_id: message.id,
                              }}
                              negativeFeedbackCallback={() => {
                                deleteStoryMessage(
                                  narrative.id,
                                  infoModal.id,
                                  message.id,
                                );
                              }}
                            />
                          </div>
                          <div className={messageStyles.translationWrapperNew}>
                              <TranslationSwitch
                                isTranslation={message.isTranslation}
                                theme='light'
                                size='medium'
                                handleTranslation={(bool) => {
                                  handleTranslation(
                                    message.id,
                                    textForTranslation,
                                    bool
                                  )
                                }}
                              />

                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <MessageTooltips scope="#storiesModal" />
            </div>
          ) : (
            <div className={styles.noData}>
              {t('There is no data to display')}
            </div>
          )}
        </>
      }
      footer={
        <div className={styles.modalControls}>
          <Paginator
            size={10}
            page={messages?.page}
            total={messages?.total}
            onPageChange={(page) => {
              setPage(page);
              setIsBulkTranslation(false);
              setIsBulkTranslationLoading(false);
            }}
            id={'storiesModal'}
            scrollContainerId={'storiesModalBody'}
          />
          {isShare === true ? "" : <div className={styles.modalActions}>
            <Tooltip content={t('Add story to case')} position="top">
              <button
                className="new-button"
                onClick={() => {
                  onAddToCase({ [infoModal.id]: true });
                }}
              >
                <AddToIcon />
              </button>
            </Tooltip>

            <Tooltip content={t('Add story to threat')} position="left">
              <button
                className="new-button"
                onClick={() => {
                  onAddToThreat({ [infoModal.id]: true });
                }}
              >
                <ShareThreatIcon />
              </button>
            </Tooltip>
          </div>}
        </div>
      }
      onClose={() => {
        onChange(false);
      }}
      className={`${styles.chartTreeModal} ${styles.storiesModal}`}
      bodyId="storiesModalBody"
    />
  );
};
